import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useSession } from './hooks/useSession'
import { AppProviders } from './context/AppProviders'
import LoadingState from './components/LoadingState'
import './App.css'

const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const LandingPage = lazy(() => import('./pages/LandingPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))

/**
 * Wraps auth pages (sign-in, sign-up). Redirects authenticated users straight
 * to the dashboard so they never land on the auth form again.
 * Shows a neutral screen while the session check is in flight.
 */
function AuthRoute({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useSession()

  if (isLoading) {
    return <LoadingState variant="fullscreen" />
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingState variant="fullscreen" />}>
      <Routes>
        {/* Auth (combined sign-in / sign-up page) */}
        <Route path="/" element={<AuthRoute><LandingPage /></AuthRoute>} />
        <Route path="/signin" element={<AuthRoute><LandingPage /></AuthRoute>} />
        <Route path="/signup" element={<AuthRoute><LandingPage /></AuthRoute>} />

        {/* Protected — wrapped in AppProviders for global state + tenant-scoped data access */}
        <Route path="/dashboard/*" element={<AppProviders><DashboardPage /></AppProviders>} />
        <Route path="/profile" element={<AppProviders><ProfilePage /></AppProviders>} />
        <Route path="/forgot-password" element={<AuthRoute><ForgotPasswordPage /></AuthRoute>} />
        <Route path="/reset-password" element={<AuthRoute><ResetPasswordPage /></AuthRoute>} />

        {/* Catch-all → root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
